import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Plus, Trash2, Edit2, Users, Check, X, ChevronDown,
  Loader2, UserPlus, UserMinus, Settings,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const COLOR_OPTIONS = [
  { key: 'blue', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  { key: 'emerald', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { key: 'purple', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  { key: 'orange', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  { key: 'red', cls: 'bg-red-100 text-red-700 border-red-200' },
  { key: 'teal', cls: 'bg-teal-100 text-teal-700 border-teal-200' },
];

const SWATCH = {
  blue: 'bg-blue-500', emerald: 'bg-emerald-500', purple: 'bg-purple-500',
  orange: 'bg-orange-500', red: 'bg-red-500', teal: 'bg-teal-500',
};

export default function SchoolRolesPage() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [members, setMembers] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [assigningId, setAssigningId] = useState(null);

  const [form, setForm] = useState({ name: '', description: '', color: 'blue', allowed_modules: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, modRes] = await Promise.all([
        api.get('/school-roles'),
        api.get('/school-roles/modules'),
      ]);
      setRoles(rolesRes.data || []);
      setModules(modRes.data || []);
    } catch { toast.error('Failed to load roles'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadMembers = async (role) => {
    setSelectedRole(role);
    setMembersLoading(true);
    try {
      const [memRes, unRes] = await Promise.all([
        api.get(`/school-roles/${role.id}/members`),
        api.get('/school-roles/staff/unassigned'),
      ]);
      setMembers(memRes.data || []);
      setUnassigned(unRes.data || []);
    } catch { toast.error('Failed to load members'); }
    finally { setMembersLoading(false); }
  };

  const openCreate = () => {
    setForm({ name: '', description: '', color: 'blue', allowed_modules: [] });
    setEditingRole(null);
    setShowForm(true);
  };

  const openEdit = (role) => {
    setForm({ name: role.name, description: role.description, color: role.color, allowed_modules: role.allowed_modules });
    setEditingRole(role);
    setShowForm(true);
    setSelectedRole(null);
  };

  const toggleModule = (key) => {
    setForm(f => ({
      ...f,
      allowed_modules: f.allowed_modules.includes(key)
        ? f.allowed_modules.filter(m => m !== key)
        : [...f.allowed_modules, key],
    }));
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('Role name is required');
    setSaving(true);
    try {
      if (editingRole) {
        await api.put(`/school-roles/${editingRole.id}`, form);
        toast.success('Role updated');
      } else {
        await api.post('/school-roles', form);
        toast.success('Role created');
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error(err?.detail || 'Failed to save role'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await api.delete(`/school-roles/${role.id}`);
      toast.success('Role deleted');
      if (selectedRole?.id === role.id) setSelectedRole(null);
      load();
    } catch (err) { toast.error(err?.detail || 'Cannot delete role'); }
  };

  const handleAssign = async (userId) => {
    if (!selectedRole) return;
    setAssigningId(userId);
    try {
      await api.post(`/school-roles/${selectedRole.id}/assign`, { user_id: userId });
      toast.success('Role assigned');
      loadMembers(selectedRole);
    } catch (err) { toast.error(err?.detail || 'Failed to assign role'); }
    finally { setAssigningId(null); }
  };

  const handleUnassign = async (userId) => {
    if (!selectedRole) return;
    try {
      await api.delete(`/school-roles/${selectedRole.id}/members/${userId}`);
      toast.success('Role unassigned');
      loadMembers(selectedRole);
    } catch (err) { toast.error(err?.detail || 'Failed to unassign'); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="school-roles-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Roles & Permissions
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Define staff roles and control which modules each role can access
          </p>
        </div>
        <button
          data-testid="create-role-btn"
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-[#2563eb] text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="w-4 h-4" /> New Role
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Roles list */}
        <div className="lg:col-span-1 space-y-3">
          {loading && (
            [...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />
            ))
          )}

          {!loading && roles.length === 0 && (
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 text-center">
              <Shield className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No roles created yet</p>
              <button onClick={openCreate} className="text-xs text-blue-600 mt-2">Create your first role →</button>
            </div>
          )}

          {roles.map(role => {
            const isActive = selectedRole?.id === role.id;
            return (
              <div
                key={role.id}
                data-testid={`role-card-${role.name.toLowerCase().replace(/\s/g, '-')}`}
                className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${
                  isActive ? 'border-blue-400 shadow-sm ring-1 ring-blue-100' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => loadMembers(role)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-3 h-3 rounded-full ${SWATCH[role.color] || 'bg-blue-500'}`} />
                    <div>
                      <p className="font-semibold text-slate-800">{role.name}</p>
                      {role.description && <p className="text-xs text-slate-500 mt-0.5">{role.description}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={e => { e.stopPropagation(); openEdit(role); }}
                      className="p-1 text-slate-400 hover:text-blue-600 rounded">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={e => { e.stopPropagation(); handleDelete(role); }}
                      className="p-1 text-slate-400 hover:text-red-500 rounded">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex flex-wrap gap-1">
                    {role.allowed_modules.slice(0, 3).map(m => (
                      <span key={m} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                        {modules.find(mod => mod.key === m)?.label || m}
                      </span>
                    ))}
                    {role.allowed_modules.length > 3 && (
                      <span className="text-[10px] bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">
                        +{role.allowed_modules.length - 3}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-slate-400">{role.member_count} member{role.member_count !== 1 ? 's' : ''}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right panel: form or members */}
        <div className="lg:col-span-2">
          {/* Create/Edit Form */}
          {showForm && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="role-form">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800">{editingRole ? 'Edit Role' : 'Create New Role'}</h3>
                <button onClick={() => setShowForm(false)}>
                  <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Name + color row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Role Name *</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Bursar, Vice Principal"
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none"
                      data-testid="role-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Color</label>
                    <div className="flex gap-2 pt-1">
                      {COLOR_OPTIONS.map(c => (
                        <button
                          key={c.key}
                          onClick={() => setForm(f => ({ ...f, color: c.key }))}
                          className={`w-6 h-6 rounded-full ${SWATCH[c.key]} border-2 transition ${
                            form.color === c.key ? 'border-slate-700 scale-110' : 'border-transparent'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Description</label>
                  <input
                    type="text"
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="What does this role do?"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none"
                    data-testid="role-description-input"
                  />
                </div>

                {/* Module permissions */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase">Module Access</label>
                    <button
                      onClick={() => setForm(f => ({
                        ...f,
                        allowed_modules: f.allowed_modules.length === modules.length
                          ? [] : modules.map(m => m.key)
                      }))}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      {form.allowed_modules.length === modules.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {modules.map(m => (
                      <label
                        key={m.key}
                        className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition text-sm ${
                          form.allowed_modules.includes(m.key)
                            ? 'bg-blue-50 border-blue-200 text-blue-700'
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <input type="checkbox" className="hidden" checked={form.allowed_modules.includes(m.key)} onChange={() => toggleModule(m.key)} />
                        <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
                          form.allowed_modules.includes(m.key) ? 'bg-blue-600' : 'bg-white border border-slate-300'
                        }`}>
                          {form.allowed_modules.includes(m.key) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        {m.label}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">{form.allowed_modules.length}/{modules.length} module{form.allowed_modules.length !== 1 ? 's' : ''} selected</p>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-[#2563eb] text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60"
                    data-testid="save-role-btn"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {editingRole ? 'Update Role' : 'Create Role'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Members panel */}
          {selectedRole && !showForm && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="role-members-panel">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${SWATCH[selectedRole.color] || 'bg-blue-500'}`} />
                  <h3 className="font-semibold text-slate-800">{selectedRole.name} — Members</h3>
                </div>
                <span className="text-xs text-slate-400">{members.length} member{members.length !== 1 ? 's' : ''}</span>
              </div>

              {membersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Current members */}
                  {members.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Current Members</p>
                      <div className="space-y-2">
                        {members.map(m => (
                          <div key={m.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600">
                                {(m.first_name || '?')[0]}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-800">{m.first_name} {m.last_name}</p>
                                <p className="text-xs text-slate-400">{m.email}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleUnassign(m.id)}
                              className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 border border-red-100 px-2 py-1 rounded-lg hover:bg-red-50"
                              data-testid="unassign-member-btn"
                            >
                              <UserMinus className="w-3 h-3" /> Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Unassigned staff */}
                  {unassigned.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Assign Staff Member</p>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {unassigned.map(u => (
                          <div key={u.id} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">
                                {(u.first_name || '?')[0]}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-700">{u.first_name} {u.last_name}</p>
                                <p className="text-xs text-slate-400">{u.email}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleAssign(u.id)}
                              disabled={assigningId === u.id}
                              className="flex items-center gap-1 text-xs text-blue-600 border border-blue-200 px-2.5 py-1.5 rounded-lg hover:bg-blue-50 disabled:opacity-60"
                              data-testid="assign-member-btn"
                            >
                              {assigningId === u.id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <UserPlus className="w-3 h-3" />}
                              Assign
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {members.length === 0 && unassigned.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-6">No staff members available to assign.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Placeholder when nothing selected */}
          {!selectedRole && !showForm && (
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-12 text-center">
              <Settings className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-500">Select a role to manage members</p>
              <p className="text-xs text-slate-400 mt-1">Or create a new role using the button above</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
